/**
 * Date-only helpers for the DatePicker. Values are ISO dates ("2026-11-01")
 * with no time zone, so every helper works on year, month and day numbers
 * and never shifts a date across midnight.
 */

export type DateParts = { year: number; month: number; day: number }

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function valid(parts: DateParts): boolean {
  return parts.year >= 1900 && parts.year <= 2200 && parts.month >= 1 && parts.month <= 12 && parts.day >= 1 && parts.day <= daysInMonth(parts.year, parts.month)
}

export function toIsoDate(parts: DateParts): string {
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`
}

export function fromIsoDate(value: string | null | undefined): DateParts | null {
  const match = (value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const parts = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
  return valid(parts) ? parts : null
}

/** "Nov 1, 2026". */
export function formatDisplayDate(value: string | null | undefined): string {
  const parts = fromIsoDate(value)
  if (!parts) return ''
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/** "Sunday, November 1, 2026", for screen readers. */
export function formatLongDate(parts: DateParts): string {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/** "November 2026". */
export function formatMonth(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Reads a typed date: "Nov 1, 2026", "November 1 2026", "1 Nov 2026",
 * "11/1/2026" (US order) or "2026-11-01". Empty text is null (no date);
 * text that is not a date is undefined.
 */
export function parseTypedDate(text: string): string | null | undefined {
  const input = text.trim().toLowerCase().replace(/,/g, ' ').replace(/\s+/g, ' ')
  if (!input) return null
  let parts: DateParts | null = null
  let match = input.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (match) parts = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
  match = parts ? null : input.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/)
  if (match) parts = { year: Number(match[3].length === 2 ? `20${match[3]}` : match[3]), month: Number(match[1]), day: Number(match[2]) }
  const monthIndex = (word: string) => word.length >= 3 ? MONTHS.findIndex((name) => name.startsWith(word.replace(/\.$/, ''))) : -1
  match = parts ? null : input.match(/^([a-z.]+) (\d{1,2})(?:st|nd|rd|th)? (\d{4})$/)
  if (match && monthIndex(match[1]) >= 0) parts = { year: Number(match[3]), month: monthIndex(match[1]) + 1, day: Number(match[2]) }
  match = parts ? null : input.match(/^(\d{1,2})(?:st|nd|rd|th)? ([a-z.]+) (\d{4})$/)
  if (match && monthIndex(match[2]) >= 0) parts = { year: Number(match[3]), month: monthIndex(match[2]) + 1, day: Number(match[1]) }
  return parts && valid(parts) ? toIsoDate(parts) : undefined
}

/** Moves a date by days, keeping it a date-only value. */
export function addDays(parts: DateParts, days: number): DateParts {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days))
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
}

/** Moves a date by months; the day clamps to the end of a shorter month (Jan 31 + 1 month = Feb 28). */
export function addMonths(parts: DateParts, months: number): DateParts {
  const index = parts.year * 12 + (parts.month - 1) + months
  const year = Math.floor(index / 12)
  const month = (index % 12) + 1
  return { year, month, day: Math.min(parts.day, daysInMonth(year, month)) }
}

/** 0 = Sunday. */
export function weekday(parts: DateParts): number {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()
}

/** The six weeks shown for a month, Sunday first, including days from the months either side. */
export function monthGrid(year: number, month: number): DateParts[][] {
  const first = { year, month, day: 1 }
  const start = addDays(first, -weekday(first))
  return Array.from({ length: 6 }, (_, week) => Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)))
}

export function compareDates(left: DateParts, right: DateParts): number {
  return toIsoDate(left).localeCompare(toIsoDate(right))
}

export function todayParts(now = new Date()): DateParts {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
}
